// (f) Helpline drawer. LOCKED during Exam Mode — content unlocks post-submit.
// Also reused as a generic right-side drawer (e.g. "Read Theory").
import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { LockIcon } from '@/components/exam/icons';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  locked?: boolean;
  children?: ReactNode;
}

export function HelplineDrawer({ open, onClose, title, locked = true, children }: DrawerProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.aside
            className="fixed right-0 top-0 z-50 flex h-full w-[88vw] max-w-sm flex-col bg-card shadow-xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            role="dialog"
            aria-label={title}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h3 className="font-semibold text-ink">{title}</h3>
              <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close">
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {locked ? (
                <div className="flex flex-col items-center gap-3 py-12 text-center text-slate-500">
                  <LockIcon className="h-10 w-10 text-slate-300" />
                  <p className="font-medium text-slate-700">Locked during the exam</p>
                  <p className="text-sm">
                    Helpline and full guidance unlock as soon as you submit your paper. Keep going —
                    you've got this.
                  </p>
                </div>
              ) : (
                children
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
